import { Box, Button, ButtonBase, FormControlLabel, InputAdornment, MenuItem, Select, Stack, Switch, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import CustomPopover, { usePopover } from '../custom-popover';
import Iconify from '../iconify';
import { useLocales } from 'src/locales';
import {
  CompareMode,
  DateRange,
  MonthRangeApplied,
  MonthRangeValue,
  capitalize,
  compareRange,
  defaultMaxDate,
  formatMonthRangeLabel,
  isMonthDisabled,
  isMonthInRange,
  last6Months,
  lastMonth,
  monthBounds,
  thisMonth,
  thisQuarter,
  thisYear,
} from 'src/utils/month-range';

export type MonthRangeSelectProps = {
  value: MonthRangeValue;
  onApply: (value: MonthRangeApplied) => void;
  maxDate?: Date;
  minDate?: Date;
};

// Figma tokens — Signal 2026 Q3, date-picker frames 7733:7565 / 7846:25495
const COLORS = {
  border: '#E3E3E3',
  dashed: 'rgba(145, 158, 171, 0.2)',
  rowBorder: '#E9ECEE',
  text: '#637381',
  textStrong: '#333C47',
  textOnFill: '#2F3843',
  compareRowBg: 'rgba(226, 229, 233, 0.16)',
  activeRowBg: 'rgba(145, 158, 171, 0.2)',
  selected: '#3D91FF',
  compare: '#3ECF8E',
  preview: 'rgba(145, 158, 171, 0.2)',
  primary: '#7635DC',
};

type MonthState = 'selected' | 'compare' | 'preview' | 'idle';

type Draft = {
  range: DateRange;
  // First click of a pending two-click selection; null when no selection is in progress.
  anchor: Date | null;
  compare: boolean;
  compareMode: CompareMode;
};

const MONTH_STYLES: Record<MonthState, { bgcolor: string; color: string }> = {
  selected: { bgcolor: COLORS.selected, color: '#FFFFFF' },
  compare: { bgcolor: COLORS.compare, color: COLORS.textOnFill },
  preview: { bgcolor: COLORS.preview, color: COLORS.textOnFill },
  idle: { bgcolor: 'transparent', color: COLORS.text },
};

const rowSx = {
  height: 46,
  px: '12px',
  justifyContent: 'flex-start',
  fontSize: 14,
  fontWeight: 500,
  color: COLORS.text,
  borderBottom: `1px dashed ${COLORS.rowBorder}`,
  width: '100%',
};

export default function MonthRangeSelect({ value, onApply, maxDate: maxDateProp, minDate }: MonthRangeSelectProps) {
  const { t, currentLang } = useLocales();
  const popover = usePopover();
  const maxDate = useMemo(() => maxDateProp ?? defaultMaxDate(), [maxDateProp]);
  const locale = currentLang?.adapterLocale;

  const initialDraft = (): Draft => ({
    range: { start: value.start, end: value.end },
    anchor: null,
    compare: value.compare,
    compareMode: value.compareMode,
  });

  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [year, setYear] = useState<number>(value.end.getFullYear());
  const [hovered, setHovered] = useState<Date | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Every opening starts from the applied value: a cancelled draft never leaks back.
    setDraft(initialDraft());
    setYear(value.end.getFullYear());
    setHovered(null);
    popover.onOpen(event);
  };

  const years = useMemo(() => {
    const last = maxDate.getFullYear();
    const first = minDate ? minDate.getFullYear() : last - 5;
    return Array.from({ length: last - first + 1 }, (_, i) => last - i);
  }, [maxDate, minDate]);

  const previewRange = draft.anchor && hovered ? monthBounds(draft.anchor, hovered, maxDate) : null;
  const compare = compareRange({ ...draft.range, compare: draft.compare, compareMode: draft.compareMode });

  const monthState = (month: Date): MonthState => {
    if (previewRange) return isMonthInRange(month, previewRange) ? 'preview' : 'idle';
    if (isMonthInRange(month, draft.range)) return 'selected';
    if (isMonthInRange(month, compare)) return 'compare';
    return 'idle';
  };

  const handleMonthClick = (month: Date) => {
    setDraft((prev) =>
      prev.anchor
        ? { ...prev, range: monthBounds(prev.anchor, month, maxDate), anchor: null }
        : { ...prev, range: monthBounds(month, month, maxDate), anchor: month }
    );
    setHovered(null);
  };

  const handlePreset = (range: DateRange) => {
    setDraft((prev) => ({ ...prev, range, anchor: null }));
    setYear(range.end.getFullYear());
  };

  const handleApply = () => {
    const range = monthBounds(draft.range.start, draft.range.end, maxDate);
    const applied = compareRange({ ...range, compare: draft.compare, compareMode: draft.compareMode });
    onApply({
      ...range,
      compare: draft.compare,
      compareMode: draft.compareMode,
      compareStart: applied?.start ?? null,
      compareEnd: applied?.end ?? null,
    });
    popover.onClose();
  };

  const compareRowSx = (mode: CompareMode) => {
    const active = draft.compare && draft.compareMode === mode;
    return {
      ...rowSx,
      bgcolor: active ? COLORS.activeRowBg : COLORS.compareRowBg,
      color: active ? COLORS.textStrong : COLORS.text,
      fontWeight: active ? 600 : 500,
    };
  };

  const months = Array.from({ length: 12 }, (_, i) => new Date(year, i, 1));

  return (
    <>
      <TextField
        size="small"
        onClick={handleOpen}
        value={formatMonthRangeLabel(value.start, value.end, locale)}
        inputProps={{ readOnly: true }}
        sx={{
          borderRadius: 1,
          width: { sm: '100%', xs: '100%', lg: 233 },
          backgroundColor: 'transparent',
          '& input': { color: 'white', cursor: 'pointer' },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="solar:calendar-line-duotone" color="rgba(145, 158, 171, 1)" width={20} sx={{ position: 'relative', top: -1 }} />
            </InputAdornment>
          ),
        }}
      />
      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        hiddenArrow
        // CustomPopover forwards `sx` to the Paper (passing slotProps would drop its positioning style).
        sx={{
          width: 486,
          p: '12px',
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" sx={{ border: `1px dashed ${COLORS.dashed}`, borderRadius: '12px', overflow: 'hidden' }}>
            <Stack sx={{ width: 200, borderRight: `1px dashed ${COLORS.dashed}` }}>
              <ButtonBase sx={rowSx} onClick={() => handlePreset(thisMonth(maxDate))}>
                {t('dashboard.filters.this_month')}
              </ButtonBase>
              <ButtonBase sx={rowSx} onClick={() => handlePreset(lastMonth(maxDate))}>
                {t('dashboard.filters.last_month')}
              </ButtonBase>
              <ButtonBase sx={rowSx} onClick={() => handlePreset(last6Months(maxDate))}>
                {t('dashboard.filters.last_6_months')}
              </ButtonBase>
              <ButtonBase sx={rowSx} onClick={() => handlePreset(thisQuarter(maxDate))}>
                {t('dashboard.filters.this_quarter')}
              </ButtonBase>
              <ButtonBase sx={rowSx} onClick={() => handlePreset(thisYear(maxDate))}>
                {t('dashboard.filters.this_year')}
              </ButtonBase>
              <Box sx={{ ...rowSx, display: 'flex', alignItems: 'center', bgcolor: COLORS.compareRowBg }}>
                <FormControlLabel
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14, color: COLORS.text, ml: 1 } }}
                  control={
                    <Switch
                      size="small"
                      checked={draft.compare}
                      onChange={(e) => setDraft((prev) => ({ ...prev, compare: e.target.checked }))}
                    />
                  }
                  label={t('dashboard.filters.enable_comparison')}
                />
              </Box>
              <ButtonBase
                sx={compareRowSx('previous_period')}
                disabled={!draft.compare}
                onClick={() => setDraft((prev) => ({ ...prev, compareMode: 'previous_period' }))}
              >
                {t('dashboard.filters.previous_period')}
              </ButtonBase>
              <ButtonBase
                sx={{ ...compareRowSx('last_year'), borderBottom: 'none' }}
                disabled={!draft.compare}
                onClick={() => setDraft((prev) => ({ ...prev, compareMode: 'last_year' }))}
              >
                {t('dashboard.filters.last_year')}
              </ButtonBase>
            </Stack>

            <Stack sx={{ width: 260 }}>
              <Box sx={{ height: 40, px: 2, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${COLORS.dashed}` }}>
                <Select
                  variant="standard"
                  disableUnderline
                  value={year}
                  inputProps={{ 'aria-label': 'Year' }}
                  onChange={(e) => setYear(Number(e.target.value))}
                  sx={{ fontSize: 14, fontWeight: 500, color: '#3D3D3D' }}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box
                // Gaps keep adjacent selected months visually separate.
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: '4px', rowGap: '4px', px: 1, py: '6px' }}
                onMouseLeave={() => setHovered(null)}
              >
                {months.map((m) => {
                  const state = monthState(m);
                  return (
                    <ButtonBase
                      key={m.getMonth()}
                      data-testid={`month-${format(m, 'yyyy-MM')}`}
                      data-state={state}
                      aria-pressed={state === 'selected'}
                      disabled={isMonthDisabled(m, maxDate, minDate)}
                      onClick={() => handleMonthClick(m)}
                      onMouseEnter={() => setHovered(m)}
                      sx={{
                        height: 50,
                        borderRadius: '8px',
                        // Smooth (continuous-curvature) corners, equivalent to an 8px radius with
                        // 60% Figma corner smoothing; browsers without corner-shape keep a plain 8px.
                        '@supports (corner-shape: squircle)': {
                          borderRadius: '13px',
                          cornerShape: 'squircle',
                        },
                        fontSize: 14,
                        fontWeight: 500,
                        ...MONTH_STYLES[state],
                        '&.Mui-disabled': { opacity: 0.4 },
                      }}
                    >
                      {capitalize(format(m, 'MMM', { locale }))}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={popover.onClose}
              sx={{ height: 30, px: '12px', borderRadius: '8px', bgcolor: COLORS.activeRowBg, color: COLORS.text, fontSize: 13, fontWeight: 600 }}
            >
              {t('dashboard.global.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              sx={{ height: 30, px: '12px', borderRadius: '8px', bgcolor: COLORS.primary, fontSize: 13, fontWeight: 600, '&:hover': { bgcolor: COLORS.primary } }}
            >
              {t('dashboard.filters.ok')}
            </Button>
          </Stack>
        </Stack>
      </CustomPopover>
    </>
  );
}
